import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Home.css';
import saveThreadToFirestore from '../utils/saveThreadToFirestore';
import { getAuth } from 'firebase/auth';
import { v4 as uuidv4 } from 'uuid';

const Home = ({ existingChatHistory = [], existingThreadName = '', existingThreadId = null }) => {
  console.log('🧠 HomeChat loaded with:');
  console.log('Thread ID:', existingThreadId);
  console.log('Thread Name:', existingThreadName);
  console.log('Existing History:', existingChatHistory);
  const navigate = useNavigate();
  const isAdmin = localStorage.getItem('isAdmin') === 'true';
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [dots, setDots] = useState('');
  const chatEndRef = useRef(null);
  const auth = getAuth();
  const user = auth.currentUser;
  const generateSmartHomeThreadName = (chatHistory) => {
    const firstUserMessage = chatHistory.find(msg => msg.sender === 'user' && msg.text.trim());

    if (firstUserMessage) {
      let preview = firstUserMessage.text.trim().slice(0, 60).replace(/\n/g, ' ');
      if (preview.length > 57) preview += '...';
      return `Alfred Chat: "${preview}"`;
    }

    return `Alfred Chat – ${new Date().toLocaleString()}`;
  };

  const [chat, setChat] = useState(() => {
    if (Array.isArray(existingChatHistory) && existingChatHistory.length > 0) {
      return existingChatHistory;
    }
    return [{ sender: 'alfred', text: 'What can I help you with today?' }];
  });

  useEffect(() => {
    if (Array.isArray(existingChatHistory) && existingChatHistory.length > 0) {
      console.log('⚡ Forcing rehydration of Alfred Chat with saved thread:', existingChatHistory);
      setChat(existingChatHistory);
    }
  }, [existingChatHistory]);

  const [threadId, setThreadId] = useState(() => existingThreadId || uuidv4());

  useEffect(() => {
    if (existingThreadId) {
      setThreadId(existingThreadId);
    }
  }, [existingThreadId]);
  const [threadName, setThreadName] = useState(() =>
    existingThreadName || generateSmartHomeThreadName(existingChatHistory)
  );

  useEffect(() => {
    if (chat.length > 1 && chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chat]);

  useEffect(() => {
    let interval;
    if (loading) {
      interval = setInterval(() => {
        setDots(prev => (prev.length < 3 ? prev + '.' : ''));
      }, 500);
    } else {
      setDots('');
    }
    return () => clearInterval(interval);
  }, [loading]);

  const handleSend = async () => {
    console.log('📤 Sending message to Alfred with chat history:', chat);
    if (!input.trim()) return;

    const userMessage = { sender: 'user', text: input };
    const updatedChat = [...chat, userMessage];
    setChat(updatedChat);
    setInput('');
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/alfred-home`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messageHistory: chat
            .map((msg) => ({
              role: msg.sender === 'user' ? 'user' : 'assistant',
              content: msg.text,
            }))
            .concat({ role: 'user', content: input }),
        }),
      });

      const data = await response.json();
      const alfredReply =
        data.result || 'Got it! You can try one of the modules below or ask me more.';

      const finalChat = [...updatedChat, { sender: 'alfred', text: alfredReply }];
      setChat(finalChat);

      // ✅ Determine correct name AFTER full chat is built
      let updatedName = threadName;
      if (!existingThreadName && chat.length === 1) {
        updatedName = generateSmartHomeThreadName(finalChat);
        setThreadName(updatedName);
      }

      // ✅ Save only once, with correct final chat + name
      await saveThreadToFirestore(user?.uid, {
        id: threadId,
        name: updatedName,
        folders: [],
        type: 'home',
        data: {
          chat: finalChat,
        },
      });

    } catch (err) {
      console.error('Error fetching Alfred response:', err);
      setChat((prev) => [
        ...prev,
        {
          sender: 'alfred',
          text: 'Sorry, something went wrong. Try again or pick one of the modules below.',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };


  const cardStyle = {
    width: '250px',
    height: '140px',
    backgroundColor: '#fff',
    borderRadius: '16px',
    boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    fontSize: '19px',
    cursor: 'pointer',
    textAlign: 'center',
    padding: '20px',
    wordBreak: 'break-word',
    fontFamily: 'Helvetica Neue, sans-serif',
  };

  const topCardsContainerStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '32px 48px',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '60px auto 40px',
    width: 'fit-content',
  };

  const titleStyle = {
    textAlign: 'center',
    fontSize: '36px',
    fontWeight: 'bold',
    marginTop: '40px',
    fontFamily: 'Helvetica Neue, sans-serif',
  };

  const subtitleStyle = {
    textAlign: 'center',
    fontSize: '20px',
    fontStyle: 'italic',
    color: '#555',
    marginTop: '12px',
    marginBottom: '40px',
    fontFamily: 'Helvetica Neue, sans-serif',
  };

  const chatContainer = {
    maxWidth: '600px',
    margin: '0 auto 60px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  };

  const bubbleStyle = {
    padding: '12px 16px',
    borderRadius: '16px',
    maxWidth: '80%',
    whiteSpace: 'pre-wrap',
    lineHeight: '1.5',
    fontSize: '16px',
  };

  const alfredBubble = {
    ...bubbleStyle,
    backgroundColor: '#f0f0f0',
    alignSelf: 'flex-start',
  };

  const userBubble = {
    ...bubbleStyle,
    backgroundColor: '#e0f0ff',
    alignSelf: 'flex-end',
  };

  const inputBox = {
    width: '94.5%',
    padding: '16px',
    borderRadius: '12px',
    border: '1px solid #ccc',
    fontSize: '16px',
    marginTop: '12px',
  };

  const buttonStyle = {
    backgroundColor: '#FFD700',
    color: '#000',
    padding: '12px 20px',
    borderRadius: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    border: 'none',
    marginTop: '10px',
    alignSelf: 'flex-end',
  };

  const suggestionStyle = {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
    marginTop: '12px',
  };

  const suggestionButton = {
    padding: '8px 16px',
    borderRadius: '12px',
    border: '1px solid #ccc',
    backgroundColor: '#fff',
    fontSize: '14px',
    cursor: 'pointer',
  };

  return (
    <div>
      <h1 style={titleStyle}>
        <span className="title-underline">Welcome to Alfred — Your AI Sales Butler</span>
      </h1>
      <p style={subtitleStyle}>
        Ready to serve as your assistant, analyst, and strategist — all rolled into one.
        <br />
        Select a module below, or tell me what you’re trying to accomplish.
      </p>

      <div style={topCardsContainerStyle}>
        <div className="card" style={cardStyle} onClick={() => navigate('/industry')}>
          🧠 Industry Research
          <br />
          <span style={{ fontSize: '16px', fontWeight: 'normal' }}>
            Quickly learn everything you need about an industry to stay current & build credibility.
          </span>
        </div>
        <div className="card" style={cardStyle} onClick={() => navigate('/build-campaign')}>
          🛠️ Prospecting Campaigns
          <br />
          <span style={{ fontSize: '16px', fontWeight: 'normal' }}>
            Craft high-converting outbound campaigns across multiple channels.
          </span>
        </div>
        <div className="card" style={cardStyle} onClick={() => navigate('/prep')}>
          📋 Meeting Preparation
          <br />
          <span style={{ fontSize: '16px', fontWeight: 'normal' }}>
            Save time preparing while maximizing every client-facing opportunity.
          </span>
        </div>
        <div className="card" style={cardStyle} onClick={() => navigate('/post-call')}>
          📝 Meeting Follow Up
          <br />
          <span style={{ fontSize: '16px', fontWeight: 'normal' }}>
            Make sure you have all your ducks in a row to keep moving fast after the meeting.
          </span>
        </div>
      </div>

      {/* 💬 Alfred Chat Section */}
      <div style={chatContainer}>
        {chat.map((msg, idx) => (
          <div key={idx} style={msg.sender === 'user' ? userBubble : alfredBubble}>
            <strong>{msg.sender === 'user' ? 'You' : 'Alfred'}:</strong><br />
            {msg.text}
          </div>
        ))}
        {loading && (
          <div style={alfredBubble}>
            <strong>Alfred:</strong><br />
            Alfred is typing{dots}
          </div>
        )}
        <div ref={chatEndRef} />
        <input
          style={inputBox}
          value={input}
          placeholder="e.g., I want to learn more about the roofing industry..."
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSend()}
        />
        <div style={{ textAlign: 'right' }}>
          <button style={buttonStyle} onClick={handleSend}>
            Ask Alfred
          </button>
        </div>

      </div>
    </div>
  );
};

export default Home;
